import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

// Rich Text Editor Controller
// Provides basic WYSIWYG editing with toolbar and placeholder support
export default class extends Controller {
  static targets = ["editor", "input", "backgroundSwatch"]

  connect() {
    this.handleEditorInput = this.handleEditorInput.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePaste = this.handlePaste.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.refreshToolbarState = this.refreshToolbarState.bind(this)

    this.editorTarget.addEventListener('input', this.handleEditorInput)
    this.editorTarget.addEventListener('keydown', this.handleKeydown)
    this.editorTarget.addEventListener('paste', this.handlePaste)
    this.editorTarget.addEventListener('focus', this.handleFocus)
    this.editorTarget.addEventListener('blur', this.handleBlur)
    this.editorTarget.addEventListener('keyup', this.refreshToolbarState)
    this.editorTarget.addEventListener('mouseup', this.refreshToolbarState)
    document.addEventListener('selectionchange', this.refreshToolbarState)
    
    // Lift a saved container tint out of the loaded value before anything else
    // reads the editor content, then initialize empty state + toolbar styling.
    this.hydrateBackground()
    this.updatePlaceholderState()
    this.refreshToolbarState()
    this.initDropdowns()
  }

  disconnect() {
    this.editorTarget.removeEventListener('input', this.handleEditorInput)
    this.editorTarget.removeEventListener('keydown', this.handleKeydown)
    this.editorTarget.removeEventListener('paste', this.handlePaste)
    this.editorTarget.removeEventListener('focus', this.handleFocus)
    this.editorTarget.removeEventListener('blur', this.handleBlur)
    this.editorTarget.removeEventListener('keyup', this.refreshToolbarState)
    this.editorTarget.removeEventListener('mouseup', this.refreshToolbarState)
    document.removeEventListener('selectionchange', this.refreshToolbarState)

    this.dropdowns?.forEach((dropdown) => dropdown.dispose())
    this.dropdowns = null
  }

  // The editor container clips its overflow to keep its rounded corners, which
  // would otherwise slice off the toolbar/footer dropdown popovers (background
  // color, Add Placeholder). Pre-create their Bootstrap instances with the fixed
  // popper strategy so each menu is positioned against the viewport and escapes
  // that clip. Pre-creating (before the data-API's first click) makes the click
  // reuse this configured instance rather than a default one.
  initDropdowns() {
    this.dropdowns = Array.from(
      this.element.querySelectorAll('[data-bs-toggle="dropdown"]')
    ).map((toggle) => Dropdown.getOrCreateInstance(toggle, {
      popperConfig: (defaultConfig) => ({ ...defaultConfig, strategy: "fixed" })
    }))
  }

  handleEditorInput() {
    this.syncContentChange()
  }

  handleKeydown(event) {
    if (this.applyListInputRule(event)) return

    const action = this.shortcutAction(event)
    if (action) this[action](event)
  }

  shortcutAction(event) {
    if (!(event.metaKey || event.ctrlKey)) return null

    const key = event.key.toLowerCase()
    const digit = event.code?.startsWith('Digit') ? event.code.slice('Digit'.length) : key
    const plainShortcut = !event.altKey && !event.shiftKey
    const shiftShortcut = event.shiftKey && !event.altKey
    const altShortcut = event.altKey && !event.shiftKey

    if (plainShortcut) {
      return {
        b: 'bold',
        i: 'italic',
        u: 'underline',
        k: 'insertLink',
        ',': 'subscript',
        '.': 'superscript'
      }[key] || null
    }

    if (shiftShortcut) {
      return {
        '7': 'orderedList',
        '8': 'unorderedList'
      }[digit] || null
    }

    if (altShortcut) {
      return {
        '0': 'paragraph',
        '1': 'heading1',
        '2': 'heading2',
        '3': 'heading3'
      }[digit] || null
    }

    return null
  }

  // Google Docs-style input rules: a marker followed by Space at the beginning
  // of a text block becomes a real list. Existing list items and mid-line
  // markers are deliberately left alone.
  applyListInputRule(event) {
    if (event.key !== ' ' || event.metaKey || event.ctrlKey || event.altKey) return false

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return false

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.startContainer)) return false

    const element = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : range.startContainer
    if (element?.closest('li, ul, ol')) return false

    const block = element?.closest('p, div, h1, h2, h3')
    const root = block && this.editorTarget.contains(block) ? block : this.editorTarget
    const markerRange = document.createRange()
    markerRange.selectNodeContents(root)
    markerRange.setEnd(range.startContainer, range.startOffset)

    const command = {
      '-': 'insertUnorderedList',
      '*': 'insertUnorderedList',
      '1.': 'insertOrderedList'
    }[markerRange.toString()]
    if (!command) return false

    event.preventDefault()
    markerRange.deleteContents()
    document.execCommand(command, false, null)
    this.syncContentChange()
    return true
  }

  syncContent() {
    this.inputTarget.value = this.serializedValue()
    this.updatePlaceholderState()
  }

  // The value persisted to the hidden input: the editor content, wrapped in the
  // tint element when a container background is set. Keeping the tint INSIDE the
  // value means every host that saves/renders the editor's HTML carries it — no
  // per-host persistence code. Matches Sanitizers::RichTextSanitizer's wrapper
  // byte-for-byte so a saved block round-trips without a spurious change.
  serializedValue() {
    const inner = this.editorTarget.innerHTML
    if (!this.currentBackground) return inner

    return `<div class="rte-tint" style="background-color: ${this.currentBackground}">${inner}</div>`
  }

  // Reads a saved tint wrapper out of the loaded value: records its color, unwraps
  // it for editing (display only — the hidden input keeps the server value as the
  // dirty-tracking baseline), and styles the editor + toolbar knob to match.
  hydrateBackground() {
    this.currentBackground = ""

    const wrapper = this.editorTarget.children.length === 1 ? this.editorTarget.children[0] : null
    if (wrapper && wrapper.classList.contains("rte-tint")) {
      this.currentBackground = this.readHexColor(wrapper.getAttribute("style"))
      wrapper.replaceWith(...wrapper.childNodes)
      this.editorTarget.style.backgroundColor = this.currentBackground
    }

    this.updateBackgroundSwatch(this.currentBackground)
  }

  readHexColor(style) {
    const match = /background-color:\s*(#[0-9a-fA-F]{3,6})/.exec(style || "")
    return match ? match[1].toLowerCase() : ""
  }

  updatePlaceholderState() {
    const isEmpty = this.editorTarget.innerHTML.trim() === '' || 
                    this.editorTarget.innerHTML === '<br>' ||
                    this.editorTarget.textContent.trim() === ''
    
    this.editorTarget.classList.toggle('is-empty', isEmpty)
  }

  handleFocus() {
    this.editorTarget.classList.add('is-focused')
    this.refreshToolbarState()
  }

  handleBlur() {
    this.editorTarget.classList.remove('is-focused')
    this.syncContent()
    this.refreshToolbarState()
  }

  handlePaste(event) {
    event.preventDefault()
    const text = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain')
    
    // Sanitize and insert
    const sanitized = this.sanitizeHtml(text)
    document.execCommand('insertHTML', false, sanitized)
    this.syncContentChange()
  }

  preserveSelection(event) {
    event.preventDefault()
  }

  sanitizeHtml(html) {
    // Create a temporary element to parse HTML
    const temp = document.createElement('div')
    temp.innerHTML = html
    
    // Remove script tags and event handlers
    temp.querySelectorAll('script, style').forEach(el => el.remove())
    temp.querySelectorAll('*').forEach(el => {
      // Remove all attributes except href on links
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on') || (attr.name !== 'href' && el.tagName !== 'A')) {
          el.removeAttribute(attr.name)
        }
      })
    })
    
    return temp.innerHTML
  }

  bold(event) {
    event.preventDefault()
    document.execCommand('bold', false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  italic(event) {
    event.preventDefault()
    document.execCommand('italic', false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  underline(event) {
    event.preventDefault()
    document.execCommand('underline', false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  subscript(event) {
    this.toggleInlineCommand(event, 'subscript')
  }

  superscript(event) {
    this.toggleInlineCommand(event, 'superscript')
  }

  unorderedList(event) {
    this.toggleList(event, 'insertUnorderedList')
  }

  orderedList(event) {
    this.toggleList(event, 'insertOrderedList')
  }

  heading1(event) {
    this.formatBlock(event, 'h1')
  }

  heading2(event) {
    this.formatBlock(event, 'h2')
  }

  heading3(event) {
    this.formatBlock(event, 'h3')
  }

  // Converts the current block back to a plain paragraph — the "Normal text"
  // toolbar button, to undo a heading and return the block to default styling.
  // Unlike the heading buttons, this always normalizes the WHOLE block (never
  // splits a partial selection), since "make this a normal paragraph" is a
  // block-level intent, not a "promote these words" one.
  paragraph(event) {
    event.preventDefault()
    document.execCommand('formatBlock', false, '<p>')
    this.editorTarget.focus()
    this.syncContentChange()
  }

  formatBlock(event, tagName) {
    event.preventDefault()
    this.applyBlockFormat(tagName)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  // Applies a block-level format (heading or paragraph). A block tag like <h2>
  // cannot cover only part of a line, so when the user selected a sub-range of a
  // single text block we SPLIT it: the selected text becomes its own heading and
  // the surrounding text stays as paragraphs — instead of retagging the whole
  // paragraph. A caret (collapsed), a full-block selection, a multi-block
  // selection, or a list/table falls back to the browser's whole-block command.
  applyBlockFormat(tagName) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    const range = selection.getRangeAt(0)
    const block = this.enclosingTextBlock(range)
    const isPartial = block && !range.collapsed && block.tagName.toLowerCase() !== tagName

    if (!isPartial) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    this.splitBlockAroundSelection(block, range, tagName)
  }

  // The single top-level text block (p/h1-3/div, a direct child of the editor)
  // that fully contains the selection, or null when the selection is outside the
  // editor, spans more than one block, or sits in a list/table.
  enclosingTextBlock(range) {
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return null

    const startBlock = this.topLevelBlock(range.startContainer)
    const endBlock = this.topLevelBlock(range.endContainer)
    if (!startBlock || startBlock !== endBlock) return null
    if (!['P', 'H1', 'H2', 'H3', 'DIV'].includes(startBlock.tagName)) return null

    return startBlock
  }

  // Walks up to the node's ancestor that is a direct child of the editor, or null
  // when the node is the editor itself or lives outside it.
  topLevelBlock(node) {
    let element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
    if (!element || element === this.editorTarget || !this.editorTarget.contains(element)) return null

    while (element.parentElement && element.parentElement !== this.editorTarget) {
      element = element.parentElement
    }
    return element.parentElement === this.editorTarget ? element : null
  }

  // Rebuilds one text block as up to three siblings: the text before the
  // selection (kept in the original tag), the selected text (in the new tag),
  // and the text after (kept in the original tag). Empty leading/trailing parts
  // are dropped, and the caret is left inside the new heading.
  splitBlockAroundSelection(block, range, tagName) {
    const originalTag = block.tagName.toLowerCase()

    const beforeRange = document.createRange()
    beforeRange.selectNodeContents(block)
    beforeRange.setEnd(range.startContainer, range.startOffset)

    const afterRange = document.createRange()
    afterRange.selectNodeContents(block)
    afterRange.setStart(range.endContainer, range.endOffset)

    const heading = this.wrapFragment(range.cloneContents(), tagName)
    if (!heading) {
      document.execCommand('formatBlock', false, `<${tagName}>`)
      return
    }

    const nodes = []
    const before = this.wrapFragment(beforeRange.cloneContents(), originalTag)
    if (before) nodes.push(before)
    nodes.push(heading)
    const after = this.wrapFragment(afterRange.cloneContents(), originalTag)
    if (after) nodes.push(after)

    block.replaceWith(...nodes)

    const restored = document.createRange()
    restored.selectNodeContents(heading)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(restored)
  }

  // Wraps a cloned fragment in `tagName`, or returns null when the fragment holds
  // no meaningful content (so an empty before/after part is skipped).
  wrapFragment(fragment, tagName) {
    if ((fragment.textContent || '').trim() === '' && !fragment.querySelector('img, br')) {
      return null
    }
    const element = document.createElement(tagName)
    element.appendChild(fragment)
    return element
  }

  toggleInlineCommand(event, command) {
    event.preventDefault()
    document.execCommand(command, false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  toggleList(event, command) {
    event.preventDefault()
    document.execCommand(command, false, null)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  insertLink(event) {
    event.preventDefault()
    const url = prompt('Enter URL:', 'https://')
    if (url) {
      document.execCommand('createLink', false, url)
      this.editorTarget.focus()
      this.syncContentChange()
    }
  }

  // Sets the background color of the whole editor container (not the selection).
  // An empty data-color clears the fill back to the default white. The color is
  // folded into the serialized value (syncContent), so the change persists and
  // re-renders wherever the editor's HTML is stored/shown.
  setBackgroundColor(event) {
    event.preventDefault()
    this.currentBackground = (event.currentTarget.dataset.color || '').toLowerCase()
    this.editorTarget.style.backgroundColor = this.currentBackground
    this.updateBackgroundSwatch(this.currentBackground)
    this.editorTarget.focus()
    this.syncContentChange()
  }

  updateBackgroundSwatch(color) {
    if (!this.hasBackgroundSwatchTarget) return
    this.backgroundSwatchTarget.style.backgroundColor = color || ''
  }

  insertPlaceholder(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.dataset.placeholder
    
    // Focus the editor
    this.editorTarget.focus()
    
    // Insert the placeholder at cursor position
    document.execCommand('insertText', false, placeholder)
    this.syncContentChange()
  }

  syncContentChange() {
    this.syncContent()
    this.refreshToolbarState()
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  refreshToolbarState() {
    const commands = this.activeCommands()

    this.toolbarButtons.forEach((button) => {
      const active = commands.has(button.dataset.richTextEditorCommand)
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
    })
  }

  activeCommands() {
    const selection = window.getSelection()
    const activeCommands = new Set()

    if (!selection || selection.rangeCount === 0 || !this.editorTarget.contains(selection.anchorNode)) {
      return activeCommands
    }

    ;[
      'bold',
      'italic',
      'underline',
      'subscript',
      'superscript',
      'insertUnorderedList',
      'insertOrderedList'
    ].forEach((command) => {
      if (document.queryCommandState(command)) activeCommands.add(command)
    })

    const block = this.closestBlock(selection.anchorNode)
    if (block) activeCommands.add(block.tagName.toLowerCase())

    const inline = this.closestInlineFormat(selection.anchorNode) || this.closestInlineFormat(selection.focusNode)
    if (inline) activeCommands.add(this.commandForInlineFormat(inline))

    return activeCommands
  }

  closestBlock(node) {
    if (!node) return null

    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node

    return element?.closest?.('h1, h2, h3, p')
  }

  closestInlineFormat(node) {
    if (!node) return null

    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node

    return element?.closest?.('sub, sup')
  }

  commandForInlineFormat(element) {
    return {
      sub: 'subscript',
      sup: 'superscript'
    }[element.tagName.toLowerCase()]
  }

  get toolbarButtons() {
    return this.element.querySelectorAll('[data-rich-text-editor-command]')
  }
}
